import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Heart, Zap, Globe, Briefcase, Mail, Plane, MessageSquare, Rocket, UserCheck, ThumbsUp, Target } from 'lucide-react';
import Footer from '@/components/Footer';
import { CAREERS_JOBS } from '@/data/careersJobs';
import PageSeo from '@/components/PageSeo';

// Flatten jobs for listing (each opening gets its own card with location)
const CURRENT_OPENINGS = CAREERS_JOBS.flatMap((job) =>
  (Array.isArray(job.openings) ? job.openings : [{ location: job.location }]).map((o) => ({
    title: job.title,
    department: job.department,
    location: o.location,
    slug: job.slug
  }))
);

export default function Careers() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const credos = [
    {
      icon: Plane,
      title: 'Travel Is for Everyone',
      description: 'We design for the people traditional travel companies ignore. Credit checks, volatile pricing, and predatory BNPL interest—we built Defera to replace all of that. Every feature we ship should make booking easier, fairer, or more accessible.'
    },
    {
      icon: MessageSquare,
      title: 'Say It Plain',
      description: 'No jargon, no fine print, no gotchas. Our customers deserve to know exactly what they\'re paying and when. We write clearly, price transparently, and explain things like we\'re talking to a friend.'
    },
    {
      icon: Rocket,
      title: 'Ship and Learn',
      description: 'We\'d rather get something in front of users and iterate than perfect it in a vacuum. Fast feedback beats long planning sessions. That doesn\'t mean sloppy—it means we prioritize learning over polish.'
    },
    {
      icon: UserCheck,
      title: 'Own Your Patch',
      description: 'Nobody\'s micromanaging. You get a problem, you solve it. If you need help, you ask. We trust people to figure things out and we give them the space to do it.'
    },
    {
      icon: ThumbsUp,
      title: 'Default to Yes',
      description: 'When someone has an idea or wants to try something new, we lean yes. Bureaucracy kills momentum. We\'d rather support a good experiment than block it with process.'
    },
    {
      icon: Target,
      title: 'Impact Over Optics',
      description: 'We care more about whether we\'re actually helping travelers than whether we look good doing it. Metrics matter—bookings completed, people who couldn\'t fly before who can now—not vanity stats.'
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Healthcare & Time Off',
      description: 'Medical, dental, and vision coverage. Solid PTO so you can actually disconnect. We\'re a startup so specifics vary, but we\'re not the kind of place that brags about "unlimited PTO" while nobody takes it.'
    },
    {
      icon: Zap,
      title: 'Learn on the Job',
      description: 'Stipend for courses, books, or conferences. We want you to level up—and we\'ll help pay for it. If there\'s something that would make you better at your role, we\'ll figure it out.'
    },
    {
      icon: Users,
      title: 'Real Humans, Real Connection',
      description: 'Remote doesn\'t mean isolated. We do offsites, virtual hangouts, and the occasional in-person meetup. Bring your family to the office when you visit. We\'re building a team, not a distributed workforce of strangers.'
    },
    {
      icon: Globe,
      title: 'Work From Anywhere',
      description: 'Based in San Francisco, CA. Your desk can be anywhere—we care about output, not face time. Async-first, timezone-aware, and no micromanaging. If you get your work done, we\'re good.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <PageSeo path="/careers" />
      <div className="mx-auto max-w-5xl px-6 py-12">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Hero Section */}
        <div className="mb-24">
          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-white/5 aspect-[21/9] mb-10 flex items-center justify-center">
            <img
              src="/images/careers/hero.jpg"
              alt="Defera team at work"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden absolute inset-0 flex items-center justify-center text-white/30 text-sm font-normal bg-white/5">
              Add hero image: /public/images/careers/hero.jpg
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 tracking-tight">
            Come build the future of travel.
          </h1>
          <p className="text-lg text-white/70 leading-relaxed max-w-2xl mb-8">
            We're a small team on a big mission: making flights accessible to everyone. No credit checks, no predatory interest—just fair payment options that actually work. If that sounds like something you want to build, we'd love to meet you.
          </p>
          <a
            href="#openings"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white border border-white/40 px-6 py-3 text-sm font-normal transition-colors"
          >
            <Briefcase className="w-4 h-4" />
            View Open Positions
          </a>
        </div>

        {/* Our Mission */}
        <div className="mb-24">
          <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-4 block">The Big Picture</span>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-8">What We're Building</h2>
          <p className="text-white/80 leading-relaxed mb-6">
            Travel shouldn't require perfect credit or a fat wallet. We built Defera because millions of people were being shut out—gig workers, students, families on a budget, anyone rebuilding their finances. Our layaway-style booking, price lock guarantee, and Defera Miles rewards exist to change that.
          </p>
          <p className="text-white/80 leading-relaxed mb-6">
            We're based in San Francisco, CA. The team is lean, the pace is fast, and the work actually matters. You'll be building features that help real people get on planes—family reunions, job relocations, dream trips that used to feel impossible.
          </p>
          <p className="text-white/80 leading-relaxed">
            If you like shipping quickly, owning your work, and building something that helps people instead of exploiting them—you'll fit right in.
          </p>
        </div>

        {/* Core Values */}
        <div className="mb-24">
          <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-4 block">What We Stand For</span>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-12">How We Work</h2>
          <p className="text-white/80 text-sm max-w-2xl mb-12 leading-relaxed">
            These aren't posters on the wall—they're how we actually operate. We use them when we're making calls, debating tradeoffs, or figuring out what to build next.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {credos.map((credo, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/[0.07] transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <credo.icon className="w-6 h-6 text-white/50 flex-shrink-0" />
                  <div>
                    <h3 className="text-base font-normal text-white mb-3">{credo.title}</h3>
                    <p className="text-sm text-white/80 leading-relaxed">{credo.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-24">
          <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-4 block">The Perks</span>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">What We Offer</h2>
          <p className="text-white/80 text-sm mb-12 max-w-2xl leading-relaxed">
            We're not a FAANG with free lunch and nap pods—but we do try to make it a place where you can do good work without burning out.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-lg p-6 md:p-8 hover:bg-white/[0.07] transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <benefit.icon className="w-6 h-6 text-white/50 flex-shrink-0" />
                  <div>
                    <h3 className="text-base font-normal text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-white/80 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Life at Defera - Photo Placeholders */}
        <div className="mb-24">
          <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-4 block">The Vibe</span>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-12">A Day in the Life</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1200px]">
            {[
              { src: '/images/careers/office1.jpg', alt: 'Defera office' },
              { src: '/images/careers/office2.jpg', alt: 'Defera workspace' },
              { src: '/images/careers/office3.jpg', alt: 'Defera space' }
            ].map((img, i) => (
              <div
                key={i}
                className="relative rounded-lg overflow-hidden border border-white/10 bg-white/5 aspect-[4/5] flex items-center justify-center"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement?.querySelector('[data-fallback]');
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
                <div
                  data-fallback
                  className="hidden absolute inset-0 flex items-center justify-center text-white/25 text-xs font-normal text-center px-4"
                >
                  Add image: {img.src}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Current Openings */}
        <div id="openings" className="mb-24 scroll-mt-8">
          <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-4 block">Open Roles</span>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">Join the Team</h2>
          <p className="text-white/80 text-sm mb-12 max-w-2xl leading-relaxed">
            We hire slowly and carefully. When we have openings, they'll show up here. In the meantime, feel free to reach out—we're always happy to meet people who care about this space.
          </p>
          {CURRENT_OPENINGS.length > 0 ? (
            <div className="space-y-4">
              {CURRENT_OPENINGS.map((job, index) => (
                <Link
                  key={index}
                  to={`/careers/${job.slug}`}
                  className="block bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/[0.07] transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                  <div>
                    <h3 className="text-base font-normal text-white">{job.title}</h3>
                    <p className="text-sm text-white/50 mt-1">{job.department} • {job.location}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-normal text-white/80 hover:text-white border border-white/30 px-4 py-2 rounded-lg transition-colors self-start sm:self-center">
                    <Briefcase className="w-4 h-4" />
                    Apply
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-lg p-12 md:p-16 text-center">
              <Briefcase className="w-12 h-12 text-white/30 mx-auto mb-6" />
              <p className="text-white/60 font-light mb-2 text-lg">We don't have any open positions right now.</p>
              <p className="text-sm text-white/60 font-normal max-w-md mx-auto mb-8">
                Check back soon—we're growing and new roles are added regularly. You can also reach out to share your background; we're always interested in meeting talented people.
              </p>
              <a
                href="mailto:careers@defera.app"
                className="inline-flex items-center gap-2 text-sm font-normal text-white/80 hover:text-white border border-white/30 px-6 py-3 rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                Get in Touch
              </a>
            </div>
          )}
        </div>

        {/* Get in Touch */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 md:p-10">
          <h3 className="text-lg font-light text-white mb-3">Want to Chat?</h3>
          <p className="text-sm text-white/80 leading-relaxed mb-6 max-w-2xl">
            No open role? No problem. If you're excited about travel, fintech, or building something that helps real people—send us a note. We're always happy to meet folks in the space.
          </p>
          <a
            href="mailto:careers@defera.app"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <Mail className="w-4 h-4" />
            careers@defera.app
          </a>
        </div>

        {/* Footer Note */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <p className="text-xs text-white/40 text-center leading-relaxed">
            Defera LLC • San Francisco, CA • Making travel accessible since 2024
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
